import { Activity, ActivityType, GetCustomTemplateQuery } from 'API';
import CaseService from 'services/case';
import CompanyService from 'services/company';
import PersonService from 'services/person';
import ProcessService from 'services/process';
import { GenerateReportPayload, UpdateTemplateInput } from 'shared/constants';
import { DeletePayload, DeleteReportInput } from 'shared/propTypes';
import { getPayload, notNullFilter } from 'shared/utilities';
import { getCustomTemplate } from 'graphql/queries';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { del as apiDel, post, put } from 'aws-amplify/api';

const { linkFullName } = PersonService;

class ActivityService {
  /** Use activities endpoing to create activities.
   *  Do not use graphql alone.
   */
  createActivity = async (
    input: ActivityInput,
    recipients: Array<string | undefined | null>,
  ) => {
    const actorType = PersonService.getPersonType();

    const body = {
      input: {
        ...input,
        actorType,
      },
      recipients: recipients
        .filter(notNullFilter),
    };
    const payload = await getPayload(body);

    // eslint-disable-next-line no-console
    console.log('Payload >>>>>>>>', payload);

    const { body: activityBody } = await post({ apiName: 'utilitiesapi', path: '/activities', options: payload }).response;

    return activityBody.json();
  };

  generateReport = async (input: GenerateReportPayload) => {
    const body = {
      ...input,
    };
    const payload = await getPayload(body);

    const { body: reportBody } = await post({ apiName: 'utilitiesapi', path: '/report', options: payload }).response;

    return reportBody.json();
  };

  updateTemplate = async (input: UpdateTemplateInput) => {
    const body = {
      ...input,
    };
    const payload = await getPayload(body);

    const { body: updateBody } = await put({ apiName: 'utilitiesapi', path: '/report', options: payload }).response;

    return updateBody.json();
  };

  deleteTemplate = async (input: DeletePayload) => {
    const body = {
      ...input,
    };
    const payload = await getPayload(body);

    const { body: deleteBody } = await apiDel({ apiName: 'utilitiesapi', path: '/template', options: payload }).response;

    return deleteBody.json();
  };

  getTemplate = async (id: string): Promise<string> => {
    try {
      const response = (await client.graphql({
        query: getCustomTemplate,
        variables: {
          id,
        },
      })) as GraphQLResult<GetCustomTemplateQuery>;

      const { data } = response;

      if (data?.getCustomTemplate) {
        return data.getCustomTemplate.name;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  deleteReport = async (input: DeleteReportInput) => {
    const body = {
      ...input,
    };

    const payload = await getPayload(body);

    const { body: delReportBody } = await apiDel({ apiName: 'utilitiesapi', path: '/report', options: payload }).response;

    return delReportBody.json();
  };

  getActorLink = (activity: Activity) => {
    const personID = PersonService.getPersonID();

    const actor = activity.actorEmployer
      || activity.actorTeamMember
      || activity.actorForeignNational;

    if (activity.actorID === personID) {
      return 'You';
    }

    if (!actor) {
      return <>—</>;
    }

    return <>{linkFullName(actor, true)}</>;
  };

  getActor = (activity: Activity):Person | null => {
    const actor = activity?.actorEmployer
      || activity?.actorTeamMember
      || activity?.actorForeignNational;

    return actor || null;
  };

  // targetCase: Case @hasOne(fields: ["directTargetCaseID"])
  // targetCompany: Company @hasOne(fields: ["directTargetCompanyID"])
  // targetEmployer: Employer @hasOne(fields: ["directTargetEmployerID"])
  // targetFileData: FileData @hasOne(fields: ["directTargetFileDataID"])
  // targetForeignNational: ForeignNational
  // @hasOne(fields: ["directTargetForeignNationalID"])
  // targetProcessRequest: ProcessRequest
  // @hasOne(fields: ["directTargetProcessRequestID"])
  // targetTask: Task @hasOne(fields: ["directTargetTaskID"])
  // targetTeamMember: TeamMember @hasOne(fields: ["directTargetTeamMemberID"])

  getDirectTargetString = (activity: Activity): string | null => {
    const personID = PersonService.getPersonID();

    if (personID === activity?.directTargetID) {
      return 'you';
    }

    if (activity?.targetCase) {
      return CaseService.fullname(activity?.targetCase);
    }

    if (activity?.targetCompany) {
      return activity?.targetCompany.name || null;
    }

    if (activity?.targetProcessRequest) {
      return ProcessService.fullname(activity?.targetProcessRequest);
    }

    if (activity?.targetEmployer) {
      return PersonService.fullname(activity?.targetEmployer);
    }

    if (activity?.targetForeignNational) {
      return PersonService.fullname(activity?.targetForeignNational);
    }

    if (activity?.targetTeamMember) {
      return PersonService.fullname(activity?.targetTeamMember);
    }

    return null;
  };

  getDirectTargetLink = (
    activity: Activity, withProfile?: boolean,
  ) => {
    const personID = PersonService.getPersonID();

    if (activity.targetCase) {
      return CaseService.linkName(activity.targetCase, true);
    }

    if (activity.targetCompany) {
      return CompanyService.linkName(activity.targetCompany);
    }

    if (activity.targetProcessRequest) {
      return ProcessService.linkName(activity.targetProcessRequest, true);
    }

    if (activity.targetReport) {
      if (activity.activityType === ActivityType.AUTOMATED_REPORT_SENT) {
        return CompanyService.linkReport(activity.targetReport);
      }

      return CompanyService.linkReports(activity.targetReport);
    }

    const target = activity.targetEmployer
      || activity.targetTeamMember
      || activity.targetForeignNational;

    if (activity.directTargetID === personID) {
      return 'you';
    }

    if (!target) {
      return <>—</>;
    }

    return <>{linkFullName(target, true, withProfile)}</>;
  };

  getIndirectTargetLink = (
    activity: Activity, withProfile?: boolean,
  ) => {
    const personID = PersonService.getPersonID();

    if (activity.indirectTargetCase) {
      return CaseService.linkName(activity.indirectTargetCase, true);
    }

    if (activity.indirectTargetCompany) {
      return CompanyService.linkName(activity.indirectTargetCompany);
    }

    if (activity.indirectTargetProcessRequest) {
      return ProcessService.linkName(
        activity.indirectTargetProcessRequest, true,
      );
    }

    const target = activity.indirectTargetEmployer
      || activity.indirectTargetTeamMember
      || activity.indirectTargetForeignNational;

    if (activity.indirectTargetID === personID) {
      return 'you';
    }

    if (!target) {
      return <>—</>;
    }

    return <>{linkFullName(target, true, withProfile)}</>;
  };

  getLinks = (activity: Activity) => {
    const ActorLink = activity?.actorID ? this.getActorLink(activity) : null;
    const DirectTargetLink = activity?.directTargetID
      ? this.getDirectTargetLink(activity)
      : null;
    const IndirectTargetLink = activity?.indirectTargetID
      ? this.getIndirectTargetLink(activity, false)
      : null;
    const OwnerLink = activity?.ownerID ? this.getOwnerLink(activity, false) : null;
    const OwnerPageLink = activity?.ownerID
      ? this.getOwnerLink(activity, true)
      : null;

    return {
      ActorLink,
      DirectTargetLink,
      IndirectTargetLink,
      OwnerLink,
      OwnerPageLink,
    };
  };

  getOwnerLink = (activity: Activity, withProfile?: boolean) => {
    if (activity.ownerCase) {
      return CaseService.linkName(activity.ownerCase, true, activity.actorID);
    }

    if (activity.ownerCompany) {
      return CompanyService.linkName(activity.ownerCompany);
    }

    if (activity.ownerProcessRequest) {
      return ProcessService.linkName(
        activity.ownerProcessRequest, true, activity.actorID,
      );
    }

    const owner = activity.ownerEmployer
      || activity.ownerTeamMember
      || activity.ownerForeignNational;

    if (!owner) {
      return <>—</>;
    }

    return <>{linkFullName(owner, true, withProfile)}</>;
  };
}

export default new ActivityService();
